import React, { useCallback, useEffect, useState } from 'react';
import { Create, useDataProvider, useGetOne, useNotify, useRedirect, useResourceContext } from 'react-admin';
import Permission from '../../components/Permission';
import WizardForm from '../../components/WizardForm';
import { PERMISSIONS } from '../../constants';

import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { getChainId } from '../../lib';
import FormSchedule from './wizardForms/FormSchedule';
import FormSummary from './wizardForms/FormSummary';
import FormLimitsExpiration from './wizardForms/FormLimitsExpiration';

import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Gift, GiftExpirationType, GiftRuleType } from '../../models';
import FormConditionKanban from './wizardForms/FormConditionKanban';
import FormCustomer from './wizardForms/FormCustomer';
import FormNotification from './wizardForms/FormNotification';
import FormPlacesConfiguration from './wizardForms/FormPlacesConfiguration';
import FormProduct from './wizardForms/FormProduct';
import { everyProductSkipsPlaces } from './wizardForms/redemptionPlaces';
import FormBasicInfo from './wizardForms/FormBasicInfo';
import { CheckChainUsesModule, ModuleKey } from '../../components';

const GiftCreate: React.FC = () => {
    const { state: params }: { state: Object } = useLocation();
    const [chainId] = useState(getChainId());
    const [productInfo, setProductInfo] = useState(null);
    const resource = useResourceContext();
    if (!chainId) { return <Typography>Selecione uma Rede</Typography>; }

    const { data: chain } = useGetOne('chains', { id: chainId });

    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();

    const [isLoading, setIsloading] = useState<boolean>(false);
    const [isPlacesLoading, setIsPlacesLoading] = useState<boolean>(false);
    const [isRulesLoading, setIsRulesLoading] = useState<boolean>(false);
    const [fuels, setFuels] = useState<{ [k: string]: any }[]>([]);
    const [completedSteps, setCompletedSteps] = React.useState<{
        [k: number]: boolean;
    }>({});

    useEffect(() => {
        //@ts-ignore
        dataProvider.getList('fuels', { sort: { field: 'name', order: 'ASC' }, pagination: { page: 1, perPage: 9999 } })
            .then(({ data }) => {
                setFuels(data as unknown as { [k: string]: any; }[]);
            });
    }, [])

    const validateBasicInfo = (formData: any) => {
        if (!formData.name || formData.name.trim() === '') {
            return { error: true, message: 'O nome do brinde é obrigatório' };
        }
        if (formData.name.length > 100) {
            return { error: true, message: 'O nome deve ter no máximo 100 caracteres' };
        }
        if (formData.description && formData.description.length > 500) {
            return { error: true, message: 'A descrição deve ter no máximo 500 caracteres' };
        }
        return { error: false };
    }

    const handleBasicInfo = (formData: any, nextStep: (index?: number) => void) => {
        nextStep();
    }

    const validateProduct = (formData: any) => {
        if (!formData.products || formData.products.length === 0) {
            return { error: true, message: 'Adicione pelo menos um produto ao brinde' };
        }

        return { error: false };
    }

    const handleProduct = (formData: any, nextStep: (index?: number) => void) => {
        nextStep();
    }

    const validatePlaceAndLimit = (formData: any) => {
        // Mesma regra do passo de locais (redemptionPlaces): se nenhuma recompensa tem local
        // a escolher, não há placeId a exigir.
        const skipPlaces = everyProductSkipsPlaces(formData.products || []);

        if (!skipPlaces && (!formData.placeIds || !formData.placeIds.length)) {
            return { error: true, message: 'Selecione um ou mais locais de troca' };
        }

        // Validar partnerValue para cada produto em postos das regras (stationIds)
        if (formData.giftRules?.length && formData.integrationPlaces && formData.products?.length) {
            // Extrair todos os stationIds das regras, exceto regras "clube_certo"
            const stationIds = formData.giftRules
                .filter((rule: any) => rule.type !== 'clube_certo')
                .flatMap((rule: any) => rule.stationIds || [])
                .filter((id: string, index: number, self: string[]) => self.indexOf(id) === index);

            // Verificar se os postos das regras têm partnerValue para todos os produtos
            for (const integrationPlace of formData.integrationPlaces) {
                // Só validar se for um posto das regras
                if (!stationIds.includes(integrationPlace.placeId)) continue;

                // Verificar cada produto
                if (integrationPlace.products && integrationPlace.products.length > 0) {
                    for (const productConfig of integrationPlace.products) {
                        // Se não tem ProductPlace automático, deve ter valor manual
                        if (!productConfig.hasProductPlace && productConfig.partnerValue == null) {
                            return {
                                error: true,
                                message: `Informe o Valor do parceiro para "${productConfig.productName}" no posto que está nas regras`
                            };
                        }
                    }
                }
            }
        }

        return { error: false };
    }

    const validateLimitsExpiration = (formData: any) => {
        if (typeof formData.limitQuantity === 'number' && formData.limitQuantity < 1) {
            return { error: true, message: 'O campo de limite de resgate deve ser maior que 1' };
        }
        if (formData.expirationType === GiftExpirationType.fixed && !formData.expirationTime) {
            return { error: true, message: 'O campo de data de expiração está vazio' };
        }
        if (formData.expirationType === GiftExpirationType.time && !formData.expirationInHours) {
            return { error: true, message: 'O campo de intervalo expiração está vazio' };
        }

        return { error: false };
    }

    const handlePlaceAndLimit = (formData: any, nextStep: (index?: number) => void) => {
        nextStep();
    }

    const handleLimitsExpiration = (formData: any, nextStep: () => void) => {
        nextStep();
    }

    const validateRules = (formData: any) => {
        if (!formData.giftRules) {
            return { error: true, message: 'Regras não definidas' };
        }
        // Verificar postos apenas para regras que precisam (abastecimento e compra)
        const rulesWithoutStations = formData.giftRules.filter(gr =>
            GiftRuleType.requiresStations(gr.type) && !gr.stationIds?.length);
        if (rulesWithoutStations.length) {
            return { error: true, message: 'Uma ou mais regras não possui postos definidos' };
        }
        const checkStartTimeAndEndTime = formData.giftRules.reduce((acc, gr) => {
            if (gr.startDate && !gr.endDate) {
                return { error: true, message: 'A data final deve ser uma data válida' };
            }
            if (!gr.startDate && gr.endDate) {
                return { error: true, message: 'A data inicial deve ser uma data válida' };
            }
            if (dayjs(gr.startDate) > dayjs(gr.endDate)) {
                return { error: true, message: 'A data final deve ser maior que a data de inicio' };
            }
            return acc;
        }, null);
        if (checkStartTimeAndEndTime) { return checkStartTimeAndEndTime; }

        return { error: false };
    }

    const handleRules = (formData: any, nextStep: (index?: number) => void) => {
        // A conversão ruleGroups → giftRules é feita automaticamente pelo FormConditionKanban
        nextStep();
    }

    const validateSchedule = (formData: any) => {
        if (formData.isSchedule) {
            // startAt é obrigatório
            if (!formData.startAt) {
                return { error: true, message: 'Defina uma data de início' };
            } else if (!moment(formData.startAt).isValid()) {
                return { error: true, message: 'Defina uma data de início válida' };
            }

            // endAt é opcional, mas se existir deve ser válida e posterior a startAt
            if (formData.endAt) {
                if (!moment(formData.endAt).isValid()) {
                    return { error: true, message: 'Defina uma data final válida' };
                } else if (moment(formData.endAt) <= moment(formData.startAt)) {
                    return { error: true, message: 'Data final não pode ser inferior a data inicial' };
                }
            }

            // Validações de frequência só se hasFrequency for true
            if (formData.hasFrequency) {
                if (!formData.frequency) {
                    return { error: true, message: 'Defina o tipo de frequência' };
                }

                if (formData.frequency === 'daily') {
                    if (!formData.startTime || !formData.endTime) {
                        return { error: true, message: 'Defina um horário de início e fim para a frequência diária' };
                    } else if (moment(formData.endTime) <= moment(formData.startTime)) {
                        return { error: true, message: 'Horário de fim não pode ser inferior ao horário de início' };
                    }
                }

                if (formData.frequency === 'weekly') {
                    if (!formData.weekSchedual || !formData.weekSchedual.some(ws => ws.isActive)) {
                        return { error: true, message: 'Ative pelo menos um dia da semana' };
                    }

                    const invalidWeekSchedules = formData.weekSchedual?.filter(ws => ws.isActive && (!ws.startTime || !ws.endTime));
                    if (invalidWeekSchedules && invalidWeekSchedules.length) {
                        return { error: true, message: 'Defina horário de início e fim para todos os dias ativos' };
                    }

                    const invalidTimeRanges = formData.weekSchedual?.filter(ws => ws.isActive && moment(ws.endTime) <= moment(ws.startTime));
                    if (invalidTimeRanges && invalidTimeRanges.length) {
                        return { error: true, message: 'Horário de fim não pode ser inferior ao horário de início' };
                    }
                }
            }
        }

        return { error: false };
    }

    const handleSchedule = (formData: any, nextStep: () => void) => {
        nextStep();
    }

    const validateCustomer = (formData: any) => {
        return { error: false };
    }

    const handleCustomer = (formData: any, nextStep: () => void) => {
        nextStep();
    }

    const validateNotification = (formData: any) => {
        // Validar Notificação de Criação
        if (formData.notificationCreation?.enabled) {
            if (!formData.notificationCreation?.title) {
                return { error: true, message: 'Defina um título para a notificação de criação' };
            }
            if (!formData.notificationCreation?.body) {
                return { error: true, message: 'Defina uma descrição para a notificação de criação' };
            }
            if (!formData.notificationCreation?.pushMessage) {
                return { error: true, message: 'Defina uma mensagem push para a notificação de criação' };
            }
            // Se sendNow = false, validar sendAt
            if (formData.notificationCreation?.sendNow === false && !formData.notificationCreation?.sendAt) {
                return { error: true, message: 'Defina uma data e hora de envio para a notificação de criação' };
            }
        }

        // Validar Notificação de Conquista
        if (formData.notificationAchievement?.enabled) {
            if (!formData.notificationAchievement?.title) {
                return { error: true, message: 'Defina um título para a notificação de conquista' };
            }
            if (!formData.notificationAchievement?.body) {
                return { error: true, message: 'Defina uma descrição para a notificação de conquista' };
            }
            if (!formData.notificationAchievement?.pushMessage) {
                return { error: true, message: 'Defina uma mensagem push para a notificação de conquista' };
            }
            // Validar delayMinutes (tempo de atraso)
            if (formData.notificationAchievement?.delayMinutes === undefined || formData.notificationAchievement?.delayMinutes === null) {
                return { error: true, message: 'Defina um tempo de envio para a notificação de conquista' };
            }
        }

        return { error: false };
    }

    const handleNotification = (formData: any, nextStep: () => void) => {
        nextStep();
    }

    const handleConcluedStep = (index) => {
        setCompletedSteps(value => ({ ...value, [index]: true }));
    }

    const handleCreate = (formData: any) => {
        const { products, placeIds, integrationPlaces, expirationType, expirationTime, expirationInHours, notificationCreation, notificationAchievement, ruleGroups, ...restFormData } = formData;

        setIsloading(true);

        // Extrair imagens de produtos virtuais e criar array separado na raiz
        // Isso resolve problemas com FormData e deep paths
        const productImages: { index: number; file: File }[] = [];

        // Remover campos desnecessários de cada produto baseado no tipo
        const cleanedProducts = products?.map((product, index) => {
            // Extrair imagem se for produto virtual com arquivo
            if (product.type === 'virtual' && product.productImageFile instanceof File) {
                productImages.push({ index, file: product.productImageFile });
            }

            // Remover campos de preview e file do produto
            // IMPORTANTE: Remover productImagePath se for base64 (preview local)
            // Manter apenas se for URL do S3 (imagem já existente - caso de clone)
            const { productImageFile, ...productWithoutFile } = product;

            // Remover productImagePath se for base64 (começa com "data:")
            if ((productWithoutFile.productImagePath && productWithoutFile.productImagePath.startsWith('data:')) || productWithoutFile.productImagePath === null) {
                delete productWithoutFile.productImagePath;
            }

            const productWithoutImage = productWithoutFile;

            // Remover campos desnecessários baseado no tipo de produto
            if (product.type === 'existing') {
                // Produto existente: remover campos de virtual e fuel discount
                const { alterPriceType, costValueType, costValue, alterPriceValue, alterPriceValueType, fuelId, productName, productDescription, productValue, discountPercentage, productImagePath, ...existingProduct } = productWithoutImage;
                return existingProduct;
            } else if (product.type === 'virtual') {
                // Produto virtual: remover campos de fuel discount
                const { alterPriceType, costValueType, costValue, alterPriceValue, alterPriceValueType, fuelId, ...virtualProduct } = productWithoutImage;
                return virtualProduct;
            } else if (product.type === 'fuelDiscount') {
                // Fuel discount: remover campos de virtual e existing
                const { productName, productDescription, productValue, discountPercentage, productId, ...fuelProduct } = productWithoutImage;
                return fuelProduct;
            }
            return productWithoutImage;
        }) || [];

        const data: any = {
            expirationType,
            products: cleanedProducts, // Incluir array de produtos sem imagens
            productImages: productImages.map(img => img.file), // Array de Files na raiz
            productImageIndexes: productImages.map(img => img.index), // Índices correspondentes
            ...restFormData
        }

        // Processar notificação de criação
        if (notificationCreation?.enabled) {
            // Limpar campos desnecessários e garantir valores corretos
            const processedNotificationCreation: any = {
                enabled: notificationCreation.enabled,
                title: notificationCreation.title,
                body: notificationCreation.body,
                pushMessage: notificationCreation.pushMessage,
                sendNow: notificationCreation.sendNow ?? true,
            };

            // Se sendNow = false, incluir sendAt
            if (notificationCreation.sendNow === false && notificationCreation.sendAt) {
                processedNotificationCreation.sendAt = notificationCreation.sendAt instanceof Date
                    ? notificationCreation.sendAt.toISOString()
                    : notificationCreation.sendAt;
            }

            data.notificationCreation = processedNotificationCreation;
        }

        // Processar notificação de conquista
        if (notificationAchievement?.enabled) {
            // Limpar campos desnecessários e garantir valores corretos
            const processedNotificationAchievement = {
                enabled: notificationAchievement.enabled,
                title: notificationAchievement.title,
                body: notificationAchievement.body,
                pushMessage: notificationAchievement.pushMessage,
                delayMinutes: Number(notificationAchievement.delayMinutes)
            };

            data.notificationAchievement = processedNotificationAchievement;
        }

        if (expirationType === GiftExpirationType.fixed) {
            data.expirationTime = expirationTime instanceof Date ? expirationTime.toISOString() : expirationTime;
        } else if (expirationType === GiftExpirationType.time) {
            data.expirationInHours = expirationInHours;
        }

        if (data.startAt && data.startAt instanceof Date) {
            data.startAt = data.startAt.toISOString();
        }
        if (data.endAt && data.endAt instanceof Date) {
            data.endAt = data.endAt.toISOString();
        }
        if (data.startTime && data.startTime instanceof Date) {
            data.startTime = data.startTime.toISOString();
        }
        if (data.endTime && data.endTime instanceof Date) {
            data.endTime = data.endTime.toISOString();
        }

        data.giftRules = formData.giftRules?.map((rule: any) => {
            // Para regras "clube_certo", manter apenas type, groupId e clubeCertoCheckPeriodDays
            if (rule.type === 'clube_certo') {
                return {
                    type: rule.type,
                    groupId: rule.groupId,
                    clubeCertoCheckPeriodDays: rule.clubeCertoCheckPeriodDays || 30
                };
            }
            // Para outras regras, remover clubeCertoCheckPeriodDays
            const { clubeCertoCheckPeriodDays, ...restRule } = rule;
            // Clone de brinde antigo traz quantity null da coluna (nullable); null é 400, ausente vale 1
            if (restRule.quantity == null) { delete restRule.quantity; }
            return restRule;
        });

        const finalData = Object.fromEntries(Object.entries(data).filter(([_, v]) => v != null));

        dataProvider.create(resource, {
            data: finalData,
        }).then(response => {
            notify('brinde criado com sucesso');
            redirect(`/${resource}`);
        }).catch(error => {
            notify(error.message || 'Erro ao criar brinde', { type: 'warning' });
        }).finally(() => {
            setIsloading(false);
        });
    }

    const getInitialValues = useCallback(() => {
        const { giftProductPlaces, dayScheduledGifts: paramsDayScheduledGifts, _scrollToTop, ...restParams } = params as { [k: string]: any } || {};

        // Coletar placeIds únicos de giftProductPlaces
        const placeIdsSet = new Set<string>();
        if (giftProductPlaces && giftProductPlaces.length > 0) {
            giftProductPlaces.forEach((gpp: any) => {
                gpp.places?.forEach((p: any) => {
                    if (p.placeId) placeIdsSet.add(p.placeId);
                });
            });
        }
        const placeIds: string[] = Array.from(placeIdsSet);

        const dayScheduledGifts = Array(7).fill({}).map((_, index) => (
            paramsDayScheduledGifts?.map(({ giftId, ...dayS }) => ({ ...dayS, isActive: true })).find(dayS => dayS.dayOfTheWeek === index) || { dayOfTheWeek: index, startTime: null, endTime: null, isActive: false })
        );

        const { dayScheduledGifts: giftDayScheduledGifts, ...rest } = Gift.createGift({ chainId, placeIds, dayScheduledGifts, giftProductPlaces, ...restParams });

        return { ...rest, weekSchedual: giftDayScheduledGifts, chainId };
    }, [fuels, chainId]);

    return (
        <CheckChainUsesModule module={ModuleKey.GIFT}>
            <Permission permission={PERMISSIONS.GIFT}>
                <Create title='Cadastrar Brinde'>
                    <WizardForm
                        sanitizeEmptyValues
                        isLoading={isLoading || isPlacesLoading || isRulesLoading}
                        completedSteps={completedSteps}
                        defaultValues={getInitialValues()}
                        handleConcluedStep={handleConcluedStep}
                        formSteps={[
                            {
                                label: "Informações Básicas",
                                component: <FormBasicInfo />,
                                validate: validateBasicInfo,
                                onNextFunction: handleBasicInfo,
                            },
                            {
                                label: "Regras",
                                component: <FormConditionKanban fuels={fuels as [{ id: string; name: string }]} chain={chain} onLoadingChange={setIsRulesLoading} />,
                                validate: validateRules,
                                onNextFunction: handleRules,
                            },
                            {
                                label: "Recompensas",
                                component: <FormProduct setProductInfo={setProductInfo} fuels={fuels} />,
                                validate: validateProduct,
                                onNextFunction: handleProduct,
                            },
                            {
                                label: "Locais de troca",
                                component: <FormPlacesConfiguration fuels={fuels} onLoadingChange={setIsPlacesLoading} />,
                                validate: validatePlaceAndLimit,
                                onNextFunction: handlePlaceAndLimit,
                            },
                            {
                                label: "Limites e Expiração",
                                component: <FormLimitsExpiration />,
                                validate: validateLimitsExpiration,
                                onNextFunction: handleLimitsExpiration,
                            },
                            {
                                label: "Agendamento",
                                component: <FormSchedule />,
                                validate: validateSchedule,
                                onNextFunction: handleSchedule,
                            },
                            {
                                label: "Clientes",
                                component: <FormCustomer />,
                                validate: validateCustomer,
                                onNextFunction: handleCustomer,
                            },
                            {
                                label: "Notificação",
                                component: <FormNotification />,
                                validate: validateNotification,
                                onNextFunction: handleNotification,
                            },
                            {
                                label: "Confirmação",
                                component: <FormSummary chain={chain} fuels={fuels} />,
                                onNextFunction: handleCreate,
                                validate: (formData) => {
                                    for (const validate of [validateBasicInfo, validateRules, validateProduct, validatePlaceAndLimit, validateLimitsExpiration, validateSchedule, validateCustomer, validateNotification]) {
                                        const result = validate(formData);
                                        if (result.error === true) {
                                            return result;
                                        }
                                    }
                                    return { error: false };
                                },
                                disableStep: (data, completedSteps) => {
                                    return [validateBasicInfo, validateRules, validateProduct, validatePlaceAndLimit].map(validate => validate(data)).some((item) => item.error);
                                }
                            },
                        ]}
                    />
                </Create>
            </Permission>
        </CheckChainUsesModule>
    );
};

export default GiftCreate;